import React, { JSX, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline'

export interface DropdownOption {
  value: string
  label: string
  leading?: React.ReactNode
  trailing?: React.ReactNode
}

interface DropdownProps {
  value: string
  options: DropdownOption[]
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

export const Dropdown = ({
  value,
  options,
  onChange,
  placeholder,
  disabled = false,
  className
}: DropdownProps): JSX.Element => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  const selected = options.find((o) => o.value === value)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: MouseEvent): void => {
      if (ref.current !== null && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  return (
    <div ref={ref} className={classNames('relative', className)}>
      <button
        type='button'
        onClick={() => { if (!disabled) setOpen((v) => !v) }}
        disabled={disabled}
        className={classNames(
          'flex w-full items-center justify-between gap-2 rounded-md border bg-dm-bg-accent/40 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand disabled:cursor-not-allowed disabled:opacity-60',
          open
            ? 'border-brand text-dm-text-primary'
            : 'border-white/10 text-dm-text-primary hover:border-white/20'
        )}
      >
        <span className='flex min-w-0 items-center gap-2 truncate'>
          {selected?.leading}
          <span className='truncate'>
            {selected?.label ?? (
              <span className='text-dm-text-accent'>{placeholder ?? '—'}</span>
            )}
          </span>
        </span>
        <ChevronDownIcon
          className={classNames(
            'size-4 shrink-0 text-dm-text-accent transition-transform',
            open && 'rotate-180'
          )}
          aria-hidden='true'
        />
      </button>

      {open && (
        <ul
          role='listbox'
          className='absolute left-0 right-0 z-20 mt-1 max-h-72 overflow-y-auto rounded-md border border-white/10 bg-dm-bg-secondary py-1 shadow-lg'
        >
          {options.length === 0
            ? (
              <li className='px-3 py-2 text-xs text-dm-text-accent'>No options.</li>
              )
            : options.map((o) => {
              const isSelected = o.value === value
              return (
                <li key={o.value}>
                  <button
                    type='button'
                    role='option'
                    aria-selected={isSelected}
                    onClick={() => { onChange(o.value); setOpen(false) }}
                    className={classNames(
                      'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors',
                      isSelected
                        ? 'bg-brand/10 text-dm-text-primary'
                        : 'text-dm-text-secondary hover:bg-white/5 hover:text-dm-text-primary'
                    )}
                  >
                    <span className='flex min-w-0 items-center gap-2 truncate'>
                      {o.leading}
                      <span className='truncate'>{o.label}</span>
                      {o.trailing}
                    </span>
                    {isSelected && (
                      <CheckIcon className='size-4 shrink-0 text-brand' aria-hidden='true' />
                    )}
                  </button>
                </li>
              )
            })}
        </ul>
      )}
    </div>
  )
}
